import { useState, useCallback, useEffect } from 'react';
import queryString from 'query-string';
import {
  NewApplicationListItem,
  NewApplicationStatus,
  ApplicationType,
} from '../types';
import { useLazyGetCustomersApplicationsListQuery } from '../store/services';

interface ListResponse {
  data: { data: { edges: { node: NewApplicationListItem }[] } };
}

let timeout: NodeJS.Timeout;

const useGetAutocompleteList = (search?: string) => {
  const [autocompleteList, setAutocompleteList] = useState<
    {
      id: string;
      name: string;
      dob: string;
      organizationId?: string;
      type: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      userId?: string;
    }[]
  >([]);
  const [findedItems, setFindedItems] = useState<NewApplicationListItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [prevQuery, setPrevQuery] = useState('');

  const [getList, { isLoading, isFetching }] =
    useLazyGetCustomersApplicationsListQuery();

  const getAutocompleteListAction = useCallback(async (query?: string) => {
    const results: {
      id: string;
      name: string;
      dob: string;
      organizationId?: string;
      type: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      userId?: string;
    }[] = [];
    const items: NewApplicationListItem[] = [];

    const strArr = query?.split(' ');

    let response;

    if (strArr && strArr?.length > 1 && strArr[1].length > 0) {
      response = (await getList(
        `?take=40&firstName=${strArr[0]}&lastName=${strArr[1]}`,
      )) as ListResponse;
    } else {
      response = (await getList(
        `?take=40${query ? `&firstName=${query}` : ''}`,
      )) as ListResponse;
    }

    if (response?.data?.data) {
      response.data.data.edges
        ?.filter(
          (item) =>
            item.node.status !== NewApplicationStatus.DECLINED &&
            item.node.type !== ApplicationType.CORPORATE,
        )
        ?.forEach((item) => {
          if (!results.find((result) => result.id === item.node.id)) {
            results.push({
              name: `${item.node.firstName} ${item.node.lastName}`,
              id: item.node.id,
              dob: item.node.dob || '',
              organizationId: item.node.organizationId || '',
              type: item.node.type,
              firstName: item?.node?.firstName || '',
              lastName: item?.node?.lastName || '',
              email: item.node?.email || '',
              phone: item.node?.phone || '',
              userId: item?.node?.userId || '',
            });
          }
        });

      response.data.data.edges
        ?.filter(
          (item) =>
            item.node.status !== NewApplicationStatus.DECLINED &&
            item.node.type !== ApplicationType.CORPORATE,
        )
        ?.forEach((item) => {
          if (!items.find((result) => result.id === item.node.id)) {
            items.push(item.node);
          }
        });
    }

    if (query && (strArr?.length as number) === 1) {
      const lastNameResponse = (await getList(
        `?take=40${query ? `&lastName=${query}` : ''}`,
      )) as ListResponse;

      if (lastNameResponse?.data?.data) {
        lastNameResponse.data.data.edges?.forEach((item) => {
          if (!results.find((result) => result.id === item.node.id)) {
            results.push({
              name: `${item.node.firstName} ${item.node.lastName}` || '',
              id: item.node.id,
              dob: item.node.dob || '',
              organizationId: item.node.organizationId || '',
              type: item.node.type,
              firstName: item?.node?.firstName || '',
              lastName: item?.node?.lastName || '',
              email: item.node?.email || '',
              phone: item.node?.phone || '',
              userId: item?.node?.userId || '',
            });
          }
        });

        lastNameResponse.data.data.edges
          ?.filter(
            (item) =>
              item.node.status !== NewApplicationStatus.DECLINED &&
              item.node.type !== ApplicationType.CORPORATE,
          )
          ?.forEach((item) => {
            if (!items.find((result) => result.id === item.node.id)) {
              items.push(item.node);
            }
          });
      }
    }

    setAutocompleteList(results);
    setFindedItems(items);
    clearTimeout(timeout);
  }, []);

  const onSearch = (query: string) => {
    clearTimeout(timeout);
    setSearchQuery(query);

    if (query || (!query && prevQuery)) {
      setPrevQuery(query);
      timeout = setTimeout(() => {
        getAutocompleteListAction(query);
      }, 300);
    }
  };

  useEffect(() => {
    const parsed = queryString.parse(search || '');

    if (!parsed.name) {
      getAutocompleteListAction();
    }
  }, [getAutocompleteListAction, search]);

  return {
    getAutocompleteListAction,
    autocompleteList,
    isLoading: isLoading || isFetching,
    onSearch,
    findedItems,
    searchQuery,
    setSearchQuery,
  };
};

export default useGetAutocompleteList;
